import { Navigate, Route } from 'react-router-dom';
import {
  createUnifiedTheme,
  genPageTheme,
  palettes,
  shapes,
  UnifiedThemeProvider,
} from '@backstage/theme';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import {
  createScaffolderFieldExtension,
  ScaffolderFieldExtensions,
} from '@backstage/plugin-scaffolder-react';
import { ProxySelectField } from './scaffolder/fields/ProxySelectField';
import { AwsAccessRequestField, validateAwsAccessRequest } from './scaffolder/fields/AwsAccessRequestField';
import { TemplateInfoPanel } from './scaffolder/fields/TemplateInfoPanel';
import { HarnessProjectPicker } from './scaffolder/fields/HarnessProjectPicker';
import { HarnessAutoOrgPicker } from './scaffolder/fields/HarnessAutoOrgPicker';
import { HarnessOrgPicker } from './scaffolder/fields/HarnessOrgPicker';
import { WorkflowOutputs } from './scaffolder/WorkflowOutputs';
import { shortcutsPlugin } from '@backstage-community/plugin-shortcuts';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { Mermaid } from 'backstage-plugin-techdocs-addon-mermaid';
import { MermaidExpand, ImageExpand } from '@internal/plugin-oneadvanced';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { apis, oidcAuthApiRef } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';

import type { ReactNode } from 'react';
import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
} from '@backstage/core-components';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { NotificationsPage } from '@backstage/plugin-notifications';
import { SignalsDisplay } from '@backstage/plugin-signals';
import { TechRadarPage } from '@backstage-community/plugin-tech-radar';
import { VisitListener } from '@backstage/plugin-home';
import { HomePage } from './components/home/HomePage';
import { LayoutPage, AdminHubPage, AdminLayoutsPage, WorkflowsPage, AdminBannersPage, ApprovalsPage, BannerBar, ToolsHubPage, MarkdownEditorPage, MarkdownConverterPage, Base64Page, JsonYamlPage, JsonCsvPage, JwtDecoderPage, UuidGeneratorPage, PasswordGeneratorPage, CronParserPage, SupportPage, FAQsPage, AdminFAQsPage } from '@internal/plugin-oneadvanced';

const ProxySelectFieldExtension = createScaffolderFieldExtension({
  name: 'ProxySelectField',
  component: ProxySelectField,
}).expose(scaffolderPlugin);

const TemplateInfoPanelExtension = createScaffolderFieldExtension({
  name: 'TemplateInfoPanel',
  component: TemplateInfoPanel,
}).expose(scaffolderPlugin);

const AwsAccessRequestFieldExtension = createScaffolderFieldExtension({
  name: 'AwsAccessRequestField',
  component: AwsAccessRequestField,
  validation: validateAwsAccessRequest,
}).expose(scaffolderPlugin);

const HarnessProjectPickerExtension = createScaffolderFieldExtension({
  name: 'HarnessProjectPicker',
  component: HarnessProjectPicker,
}).expose(scaffolderPlugin);

const HarnessAutoOrgPickerExtension = createScaffolderFieldExtension({
  name: 'HarnessAutoOrgPicker',
  component: HarnessAutoOrgPicker,
}).expose(scaffolderPlugin);

const HarnessOrgPickerExtension = createScaffolderFieldExtension({
  name: 'HarnessOrgPicker',
  component: HarnessOrgPicker,
}).expose(scaffolderPlugin);

const oneAdvancedTheme = createUnifiedTheme({
  palette: {
    ...palettes.light,
    primary: { main: '#E6007E' },
    secondary: { main: '#F47920' },
    background: {
      default: '#F5F5F5',
      paper: '#FFFFFF',
    },
    navigation: {
      background: '#FFFFFF',
      indicator: '#E6007E',
      color: '#1A1A1A',
      selectedColor: '#E6007E',
      navItem: { hoverBackground: '#F9E6F3' },
      submenu: { background: '#F5F5F5' },
    },
    pinSidebarButton: { icon: '#E6007E', background: '#F5F5F5' },
    tabbar: { indicator: '#E6007E' },
  },
  defaultPageTheme: 'home',
  pageTheme: {
    home: genPageTheme({ colors: ['#F47920', '#FFAB40'], shape: shapes.wave }),
    documentation: genPageTheme({ colors: ['#F47920', '#FFAB40'], shape: shapes.wave2 }),
    tool: genPageTheme({ colors: ['#F47920', '#FFAB40'], shape: shapes.round }),
    service: genPageTheme({ colors: ['#F47920', '#FFAB40'], shape: shapes.wave }),
    website: genPageTheme({ colors: ['#F47920', '#FFAB40'], shape: shapes.wave }),
    library: genPageTheme({ colors: ['#F47920', '#FFAB40'], shape: shapes.wave2 }),
    other: genPageTheme({ colors: ['#F47920', '#FFAB40'], shape: shapes.wave }),
    api: genPageTheme({ colors: ['#F47920', '#FFAB40'], shape: shapes.wave2 }),
    app: genPageTheme({ colors: ['#F47920', '#FFAB40'], shape: shapes.wave }),
    default: genPageTheme({ colors: ['#F47920', '#FFAB40'], shape: shapes.wave }),
  },
});

const oneAdvancedDarkTheme = createUnifiedTheme({
  palette: {
    ...palettes.dark,
    primary: { main: '#F47920' },
    secondary: { main: '#E6007E' },
    background: {
      default: '#1C1C1E',
      paper: '#2C2C2E',
    },
    navigation: {
      background: '#2C2C2E',
      indicator: '#F47920',
      color: '#C8C8C8',
      selectedColor: '#F47920',
      navItem: { hoverBackground: '#383838' },
      submenu: { background: '#363638' },
    },
    pinSidebarButton: { icon: '#F47920', background: '#363638' },
    tabbar: { indicator: '#F47920' },
  },
  defaultPageTheme: 'home',
  pageTheme: {
    home: genPageTheme({ colors: ['#3A3A3C', '#2C2C2E'], shape: shapes.wave }),
    documentation: genPageTheme({ colors: ['#3A3A3C', '#2C2C2E'], shape: shapes.wave2 }),
    tool: genPageTheme({ colors: ['#3A3A3C', '#2C2C2E'], shape: shapes.round }),
    service: genPageTheme({ colors: ['#3A3A3C', '#2C2C2E'], shape: shapes.wave }),
    website: genPageTheme({ colors: ['#3A3A3C', '#2C2C2E'], shape: shapes.wave }),
    library: genPageTheme({ colors: ['#3A3A3C', '#2C2C2E'], shape: shapes.wave2 }),
    other: genPageTheme({ colors: ['#3A3A3C', '#2C2C2E'], shape: shapes.wave }),
    api: genPageTheme({ colors: ['#3A3A3C', '#2C2C2E'], shape: shapes.wave2 }),
    app: genPageTheme({ colors: ['#3A3A3C', '#2C2C2E'], shape: shapes.wave }),
    default: genPageTheme({ colors: ['#3A3A3C', '#2C2C2E'], shape: shapes.wave }),
  },
});

const app = createApp({
  apis,
  plugins: [shortcutsPlugin],
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  components: {
    SignInPage: props => (
      <SignInPage
        {...props}
        providers={[
          // Guest login is local-dev only — the backend guest module refuses tokens
          // in production, but hiding the button avoids confusing UX.
          ...(process.env.NODE_ENV === 'development' ? (['guest'] as const) : []),
          {
            id: 'oidc',
            title: 'OneAdvanced SSO',
            message: 'Sign in with your @oneadvanced.com account',
            apiRef: oidcAuthApiRef,
          },
        ]}
      />
    ),
  },
  themes: [
    {
      id: 'oneadvanced-light',
      title: 'OneAdvanced Light',
      variant: 'light' as const,
      icon: <></>,
      Provider: ({ children }: { children: ReactNode }) => (
        <UnifiedThemeProvider theme={oneAdvancedTheme}>{children}</UnifiedThemeProvider>
      ),
    },
    {
      id: 'oneadvanced-dark',
      title: 'OneAdvanced Dark',
      variant: 'dark' as const,
      icon: <></>,
      Provider: ({ children }: { children: ReactNode }) => (
        <UnifiedThemeProvider theme={oneAdvancedDarkTheme}>{children}</UnifiedThemeProvider>
      ),
    },
  ],
  __experimentalTranslations: {
    resources: [
      {
        $$type: '@backstage/TranslationMessages' as const,
        id: 'catalog',
        full: false,
        messages: {
          'indexPage.title': 'Catalog',
        },
      },
    ],
  },
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<Navigate to="home" />} />
    <Route path="/home" element={<HomePage />} />
    <Route path="/workflows" element={<WorkflowsPage />} />
    <Route path="/catalog" element={<CatalogIndexPage />} />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<LayoutPage contentType="docs" pageTitle="Docs" />} />
    <Route path="/techdocs" element={<TechDocsIndexPage />} />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
        <Mermaid />
        <MermaidExpand />
        <ImageExpand />
      </TechDocsAddons>
    </Route>
    <Route path="/create" element={<ScaffolderPage headerOptions={{ title: 'Workflows', subtitle: 'Self-service workflows for onboarding, provisioning, and access management.' }} components={{ EXPERIMENTAL_TemplateOutputsComponent: WorkflowOutputs }} />}>
      <ScaffolderFieldExtensions>
        <TemplateInfoPanelExtension />
        <ProxySelectFieldExtension />
        <AwsAccessRequestFieldExtension />
        <HarnessProjectPickerExtension />
        <HarnessAutoOrgPickerExtension />
        <HarnessOrgPickerExtension />
      </ScaffolderFieldExtensions>
    </Route>
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/notifications" element={<NotificationsPage />} />
    <Route path="/oneadvanced-admin" element={<RequirePermission permission={catalogEntityCreatePermission}><AdminHubPage /></RequirePermission>} />
    <Route path="/oneadvanced-admin/layouts/:contentType" element={<RequirePermission permission={catalogEntityCreatePermission}><AdminLayoutsPage /></RequirePermission>} />
    <Route path="/oneadvanced-admin/banners" element={<RequirePermission permission={catalogEntityCreatePermission}><AdminBannersPage /></RequirePermission>} />
    <Route path="/approvals" element={<ApprovalsPage />} />
    <Route path="/support" element={<SupportPage />} />
    <Route path="/faqs" element={<FAQsPage />} />
    <Route path="/oneadvanced-admin/faqs" element={<RequirePermission permission={catalogEntityCreatePermission}><AdminFAQsPage /></RequirePermission>} />
    <Route path="/tools" element={<ToolsHubPage />} />
    <Route path="/tools/markdown/editor" element={<MarkdownEditorPage />} />
    <Route path="/tools/markdown/converter" element={<MarkdownConverterPage />} />
    <Route path="/tools/data/base64" element={<Base64Page />} />
    <Route path="/tools/data/json-yaml" element={<JsonYamlPage />} />
    <Route path="/tools/data/json-csv" element={<JsonCsvPage />} />
    <Route path="/tools/data/jwt" element={<JwtDecoderPage />} />
    <Route path="/tools/misc/uuid" element={<UuidGeneratorPage />} />
    <Route path="/tools/misc/password" element={<PasswordGeneratorPage />} />
    <Route path="/tools/misc/cron" element={<CronParserPage />} />
    <Route
      path="/tech-radar"
      element={
        <TechRadarPage
          id="enterprise"
          title="Enterprise Tech Radar"
          subtitle="What we invest in, adopt, assess, maintain, and retire — across platforms, languages, tooling, and data."
          pageTitle="Enterprise Tech Radar"
        />
      }
    />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <SignalsDisplay />
    <AppRouter>
      <VisitListener />
      <Root>
        <BannerBar />
        {routes}
      </Root>
    </AppRouter>
  </>,
);
